import { PongerPromiseClient } from "../generated/bpong_grpc_web_pb";
import {
    AdvanceTournamentRequest,
    BracketType as BracketTypeEnum,
    CreateTournamentRequest,
    DeleteTournamentRequest,
    EliminateTeamRequest,
    ReinstateTeamRequest,
    GetTournamentRequest,
    ListTournamentsRequest,
    Match as MatchMessage,
    ParticipantInput,
    Team,
    TeamInput,
    TeamMember as TeamMemberMessage,
    Tournament as TournamentMessage,
    TournamentStatus as TournamentStatusEnum,
    TournamentStyle as TournamentStyleEnum,
    TournamentVisibility as TournamentVisibilityEnum,
    UpdateMatchRequest,
    UpdateTournamentRequest,
} from "../generated/bpong_pb";
import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import GRPC_BACKEND_URL, { GRPC_CLIENT_OPTIONS } from "../grpcConfig";
import { AuthUser } from "../context/AuthContext";
import { buildUserMessage } from "./drinks/utils/user";

const pongClient = new PongerPromiseClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);

const fromTimestamp = (timestamp?: Timestamp): Date | null => {
    if (!timestamp) {
        return null;
    }

    const millis = timestamp.getSeconds() * 1000 + Math.floor(timestamp.getNanos() / 1_000_000);
    return new Date(millis);
};

export type TournamentStyle = TournamentStyleEnum;
export type TournamentStatus = TournamentStatusEnum;
type BracketType = BracketTypeEnum;
export type TournamentVisibility = TournamentVisibilityEnum;


type TournamentTeamMember = {
    teamMemberId: number;
    userId: number | null;
    displayName: string;
};

export type TournamentTeam = {
    teamId: number;
    teamName: string;
    members: TournamentTeamMember[];
    isEliminated: boolean;
};

export type TournamentMatch = {
    matchId: number;
    tournamentId: number;
    teamAId: number;
    teamBId: number;
    winnerTeamId: number | null;
    roundNumber: number | null;
    scheduledAt: Date | null;
    playedAt: Date | null;
    isCancelled: boolean;
    bracketType: BracketType;
};

export type Tournament = {
    tournamentId: number;
    tournamentName: string;
    eventId: number | null;
    createdBy: number;
    createdAt: Date;
    teams: TournamentTeam[];
    matches: TournamentMatch[];
    style: TournamentStyle;
    status: TournamentStatus;
    visibility: TournamentVisibility;
    requiresPassword: boolean;
    isOwner: boolean;
};

type CreateTournamentParticipantInput = {
    displayName: string;
    userId?: number | null;
};

type CreateTournamentTeamInput = {
    teamName: string;
    participants: CreateTournamentParticipantInput[];
};

export type CreateTournamentInput = {
    tournamentName: string;
    teams: CreateTournamentTeamInput[];
    eventId?: number | null;
    style: TournamentStyle;
    visibility: TournamentVisibility;
    password?: string | null;
};

export type UpdateMatchPayload = {
    teamAId?: number;
    teamBId?: number;
    winnerTeamId?: number | null;
};

type UpdateTournamentPayload = {
    status?: TournamentStatus;
    style?: TournamentStyle;
    visibility?: TournamentVisibility;
    password?: string | null;
};

const mapTeamMember = (message: TeamMemberMessage): TournamentTeamMember => ({
    teamMemberId: message.getTeamMemberId(),
    userId: message.getUserId() > 0 ? message.getUserId() : null,
    displayName: message.getDisplayName(),
});

const mapTeam = (message: Team): TournamentTeam => ({
    teamId: message.getTeamId(),
    teamName: message.getTeamName(),
    members: message.getMembersList().map(mapTeamMember),
    isEliminated: message.getIsEliminated(),
});

const mapMatch = (message: MatchMessage): TournamentMatch => ({
    matchId: message.getMatchId(),
    tournamentId: message.getTournamentId(),
    teamAId: message.getTeamAId(),
    teamBId: message.getTeamBId(),
    winnerTeamId: message.getWinnerTeamId() || null,
    roundNumber: message.getRoundNumber() || null,
    scheduledAt: fromTimestamp(message.getTsScheduled()),
    playedAt: fromTimestamp(message.getTsPlayed()),
    isCancelled: message.getIsCancelled(),
    bracketType: message.getBracketType(),
});

const mapTournament = (message: TournamentMessage): Tournament => ({
    tournamentId: message.getTournamentId(),
    tournamentName: message.getTournamentName(),
    eventId: message.getEventId() > 0 ? message.getEventId() : null,
    createdBy: message.getCreatedBy(),
    createdAt: fromTimestamp(message.getTsCreated()) ?? new Date(),
    teams: message.getTeamsList().map(mapTeam),
    matches: message.getMatchesList().map(mapMatch),
    style: message.getTournamentStyle(),
    status: message.getStatus(),
    visibility: message.getVisibility(),
    requiresPassword: message.getRequiresPassword(),
    isOwner: message.getIsOwner(),
});

export const createTournament = async (
    input: CreateTournamentInput,
    creator: AuthUser
): Promise<Tournament> => {
    const request = new CreateTournamentRequest();
    request.setTournamentName(input.tournamentName.trim());
    request.setCreatedBy(creator.userId);
    request.setUser(buildUserMessage(creator));

    if (input.eventId && input.eventId > 0) {
        request.setEventId(input.eventId);
    }

    input.teams.forEach((team) => {
        const teamMessage = new TeamInput();
        teamMessage.setTeamName(team.teamName.trim());

        team.participants.forEach((participant) => {
            const participantMessage = new ParticipantInput();
            if (participant.userId && participant.userId > 0) {
                participantMessage.setUserId(participant.userId);
            }
            participantMessage.setDisplayName(participant.displayName.trim());
            teamMessage.addParticipants(participantMessage);
        });

        request.addTeams(teamMessage);
    });

    request.setTournamentStyle(input.style);
    request.setVisibility(input.visibility);

    if (input.password && input.password.trim().length > 0) {
        request.setAccessPassword(input.password.trim());
    } else {
        request.clearAccessPassword();
    }

    const response = await pongClient.createTournament(request, {});
    return mapTournament(response);
};

export const listTournaments = async (user?: AuthUser, eventId?: number | null): Promise<Tournament[]> => {
    const request = new ListTournamentsRequest();
    if (eventId && eventId > 0) {
        request.setEventId(eventId);
    }
    if (user) {
        request.setUser(buildUserMessage(user));
    }

    const response = await pongClient.listTournaments(request, {});
    return response.getTournamentsList().map(mapTournament);
};

export const getTournament = async (
    tournamentId: number,
    user?: AuthUser | null,
    password?: string
): Promise<Tournament> => {
    const request = new GetTournamentRequest();
    request.setTournamentId(tournamentId);
    if (user) {
        request.setUser(buildUserMessage(user));
    } else {
        request.clearUser();
    }
    if (password) {
        request.setAccessPassword(password);
    } else {
        request.clearAccessPassword();
    }

    const response = await pongClient.getTournament(request, {});
    return mapTournament(response);
};

export const updateMatch = async (matchId: number, payload: UpdateMatchPayload, user: AuthUser): Promise<TournamentMatch> => {
    const request = new UpdateMatchRequest();
    request.setMatchId(matchId);
    request.setUser(buildUserMessage(user));

    if (payload.teamAId !== undefined) {
        request.setTeamAId(payload.teamAId);
    }

    if (payload.teamBId !== undefined) {
        request.setTeamBId(payload.teamBId);
    }

    if (payload.winnerTeamId !== undefined) {
        const value = payload.winnerTeamId ?? 0;
        request.setWinnerTeamId(value);
    }

    const response = await pongClient.updateMatch(request, {});
    return mapMatch(response);
};

export const updateTournament = async (
    tournamentId: number,
    payload: UpdateTournamentPayload,
    user: AuthUser
): Promise<Tournament> => {
    const request = new UpdateTournamentRequest();
    request.setTournamentId(tournamentId);
    request.setUser(buildUserMessage(user));

    if (payload.status !== undefined) {
        request.setStatus(payload.status);
    }

    if (payload.style !== undefined) {
        request.setTournamentStyle(payload.style);
    }

    if (payload.visibility !== undefined) {
        request.setVisibility(payload.visibility);
    }

    if (payload.password !== undefined) {
        if (payload.password === null || payload.password === "") {
            request.clearAccessPassword();
        } else {
            request.setAccessPassword(payload.password);
        }
    }

    const response = await pongClient.updateTournament(request, {});
    return mapTournament(response);
};

export const eliminateTeam = async (tournamentId: number, teamId: number, user: AuthUser): Promise<Tournament> => {
    const request = new EliminateTeamRequest();
    request.setTournamentId(tournamentId);
    request.setTeamId(teamId);
    request.setUser(buildUserMessage(user));

    const response = await pongClient.eliminateTeam(request, {});
    return mapTournament(response);
};

export const reinstateTeam = async (tournamentId: number, teamId: number, user: AuthUser): Promise<Tournament> => {
    const request = new ReinstateTeamRequest();
    request.setTournamentId(tournamentId);
    request.setTeamId(teamId);
    request.setUser(buildUserMessage(user));

    const response = await pongClient.reinstateTeam(request, {});
    return mapTournament(response);
};

export const advanceTournament = async (tournamentId: number, user: AuthUser): Promise<Tournament> => {
    const request = new AdvanceTournamentRequest();
    request.setTournamentId(tournamentId);
    request.setUser(buildUserMessage(user));

    const response = await pongClient.advanceTournament(request, {});
    return mapTournament(response);
};

export const deleteTournament = async (tournamentId: number, user: AuthUser): Promise<void> => {
    const request = new DeleteTournamentRequest();
    request.setTournamentId(tournamentId);
    request.setUser(buildUserMessage(user));
    await pongClient.deleteTournament(request, {});
};
